import React, { useState } from "react";
import { CalendarRange, Check, Pencil, RotateCcw, X } from "lucide-react";
import { AppCard, IconButton, Input, SegmentedControl } from "../ui";
import { DEFAULT_CLASS_MINUTES } from "./classPlanner";
import { formatMinutes } from "./syllabusModel";

const PRESETS = [60, 90, 120, 180];

/**
 * ClassPreview — the classes a module-wise syllabus forms, at a class length.
 * This is what a batch gets when it starts (a batch with class timings uses
 * its own length). The admin can name a class here, which pins it to start
 * at its first topic.
 *
 * Props:
 *   classes        — classPlanner.planClasses() result
 *   classMinutes   — the syllabus's class length (null = 60)
 *   onClassMinutes(n | null)
 *   onRename(startTopicUid, title | "")   — "" goes back to the automatic name
 */
const ClassPreview = ({ classes, classMinutes, onClassMinutes, onRename }) => {
  const [renaming, setRenaming] = useState(null); // { uid, value }
  const length = classMinutes || DEFAULT_CLASS_MINUTES;
  const total = classes.reduce((n, c) => n + c.minutes, 0);

  const commit = () => {
    if (renaming) onRename(renaming.uid, renaming.value.trim());
    setRenaming(null);
  };

  let lastModule = -1;
  return (
    <AppCard as="section" aria-labelledby="sb-classes-heading">
      <div className="tw-flex tw-flex-col tw-gap-4 tw-p-4">
        <div className="tw-flex tw-flex-col tw-gap-1">
          <h2 id="sb-classes-heading" className="tw-m-0 tw-flex tw-items-center tw-gap-2 tw-text-[0.9375rem] tw-font-semibold tw-text-content">
            <CalendarRange size={16} aria-hidden="true" className="tw-text-primary" /> Classes
          </h2>
          <p className="tw-m-0 tw-text-xs tw-text-content-muted">
            Topics are grouped into classes of this length, in order. A batch with class timings uses its own.
          </p>
        </div>

        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
          <SegmentedControl
            label="Class length"
            value={PRESETS.includes(length) ? length : null}
            onChange={(v) => onClassMinutes(v)}
            options={PRESETS.map((m) => ({ value: m, label: formatMinutes(m) }))}
          />
          <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-content-muted">
            or
            <Input
              size="sm"
              type="number"
              min={15}
              max={600}
              step={5}
              fullWidth={false}
              className="tw-w-20 tw-tabular-nums"
              value={classMinutes ?? ""}
              placeholder={String(DEFAULT_CLASS_MINUTES)}
              onChange={(e) => onClassMinutes(e.target.value === "" ? null : Number(e.target.value))}
              aria-label="Class length in minutes"
            />
            min
          </span>
        </div>

        <p className="tw-m-0 tw-text-sm tw-font-semibold tw-text-content" aria-live="polite">
          {classes.length} class{classes.length === 1 ? "" : "es"}
          <span className="tw-font-normal tw-text-content-muted"> · {formatMinutes(total)} of teaching</span>
        </p>

        {classes.length > 0 && (
          <ol className="tw-m-0 tw-flex tw-max-h-[55vh] tw-list-none tw-flex-col tw-gap-1 tw-overflow-y-auto tw-p-0 tw-pe-1">
            {classes.map((c) => {
              const header = c.moduleIndex !== lastModule;
              lastModule = c.moduleIndex;
              const editing = renaming?.uid === c.startTopicUid;
              return (
                <React.Fragment key={c.seq}>
                  {header && (
                    <li aria-hidden="true" className="tw-mt-2 tw-truncate tw-text-[0.6875rem] tw-font-semibold tw-uppercase tw-tracking-caps tw-text-content-subtle first:tw-mt-0">
                      Module {c.moduleIndex + 1} · {c.moduleTitle || "Untitled"}
                    </li>
                  )}
                  <li className="tw-flex tw-items-start tw-gap-2 tw-rounded-md tw-px-2 tw-py-1.5 hover:tw-bg-surface-hover">
                    <span className="tw-mt-0.5 tw-w-7 tw-shrink-0 tw-text-xs tw-font-semibold tw-text-content-subtle">{c.seq}</span>
                    <span className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col">
                      {editing ? (
                        <Input
                          size="sm"
                          autoFocus
                          value={renaming.value}
                          onChange={(e) => setRenaming({ ...renaming, value: e.target.value })}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") { e.preventDefault(); commit(); }
                            if (e.key === "Escape") setRenaming(null);
                          }}
                          aria-label={`Name class ${c.seq}`}
                          maxLength={200}
                        />
                      ) : (
                        <span className="tw-truncate tw-text-[0.8125rem] tw-font-medium tw-text-content" title={c.title}>{c.title}</span>
                      )}
                      <span className="tw-truncate tw-text-xs tw-text-content-muted">
                        {formatMinutes(c.minutes)}
                        {c.topics.length > 1 ? ` · ${c.topics.map((t) => t.name).join(", ")}` : ""}
                      </span>
                    </span>
                    {editing ? (
                      <>
                        <IconButton size="xs" icon={Check} label="Save the name" onClick={commit} />
                        <IconButton size="xs" icon={X} label="Cancel" onClick={() => setRenaming(null)} />
                      </>
                    ) : (
                      <>
                        {c.titleOverridden && (
                          <IconButton size="xs" icon={RotateCcw} label={`Use the automatic name for class ${c.seq}`} onClick={() => onRename(c.startTopicUid, "")} />
                        )}
                        <IconButton size="xs" icon={Pencil} label={`Name class ${c.seq}`} onClick={() => setRenaming({ uid: c.startTopicUid, value: c.title })} />
                      </>
                    )}
                  </li>
                </React.Fragment>
              );
            })}
          </ol>
        )}
      </div>
    </AppCard>
  );
};

export default ClassPreview;
